import type { ClassroomPage, ClassroomSession } from "./types";
import { SCHOOLS_HIDE_QR_CODE } from "../../constants";

// What the manage page offers for a booking, from the classroom answer.
// The rules are v1's (BookingManageView + VirtualClassroom +
// BookingReceiptCard), gathered in one pure place so they can be tested.


export type ManageState = {
  isBundle: boolean;
  isCancelled: boolean;
  /** The school cancelled the session itself. */
  sessionCancelled: boolean;
  isOnline: boolean;
  canCancel: boolean;
  canReschedule: boolean;
  canRequestRefund: boolean;
  /** "This session was cancelled by the school" with what the customer can do. */
  showOrphanedBanner: boolean;
  showQrCode: boolean;
  reviewsEnabled: boolean;
};

export function deriveManageState(page: ClassroomPage): ManageState {
  const { booking } = page;
  const isBundle = booking.type === "bundle_booking";
  const isCancelled = Boolean(booking.cancelled || booking.cancelledObject?.isCancelled);
  const sessionCancelled = Boolean(page.session?.cancelled);
  // A bundle booking is a set of dates: it is managed through its dates
  // and the bundle, never cancelled, rescheduled or refunded as one.
  const canCancel = !isBundle && Boolean(page.canBeCancelled);
  const canReschedule = !isBundle && Boolean(page.canBeRescheduled);
  const canRequestRefund = !isBundle && Boolean(page.canBeRefundedByCustomer);
  return {
    isBundle,
    isCancelled,
    sessionCancelled,
    isOnline: Boolean(page.onlineJoinLink),
    canCancel,
    canReschedule,
    canRequestRefund,
    showOrphanedBanner: sessionCancelled && (canReschedule || canRequestRefund),
    showQrCode: Boolean(booking._id) && !isCancelled && !SCHOOLS_HIDE_QR_CODE.includes(page.school?._id),
    // The API sends the course without `isWidgetReviewsEnabled` when the
    // school never set it; v1 then hid the review prompt, and so does this.
    reviewsEnabled: Boolean(page.course?.isWidgetReviewsEnabled),
  };
}

/** A bundle's dates in order, optionally only those still to come (the
 * receipt shows the next three; "Get ready" lists them all). */
export function bundleDates(sessions: ClassroomSession[] | undefined, opts: { upcomingOnly: boolean; now: number; limit?: number }) {
  const sorted = [...(sessions ?? [])].sort(
    (a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime(),
  );
  const shown = opts.upcomingOnly ? sorted.filter((s) => new Date(s.startDateTime).getTime() > opts.now) : sorted;
  return opts.limit ? shown.slice(0, opts.limit) : shown;
}
